import { Controller } from 'react-hook-form';
import { Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { CURRENCY_OPTIONS } from '../../../../constants/roles';
import { StyleObj } from '../../../../@types';
import StyledInput from '../atoms/StyledInput';
import StyledSelect from '../atoms/StyledSelect';
import { useCreateBOUsersForm } from '../../../../hooks/useCreateBOUsersForm';
import CloseIcon from '@mui/icons-material/Close';

const styles: StyleObj = {
  container: {
    backgroundColor: 'neutral.800',
    minHeight: '100%',
    height: '100%',
  },
  header: {
    px: 2,
    pt: 1.5,
    pb: 2.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: 'neutral.100',
  },
  closeButton: {
    color: 'neutral.300',
    p: 0.5,
  },
  section: {
    border: '1px solid',
    borderColor: 'neutral.25',
    borderRadius: '10px',
    backgroundColor: 'neutral.600',
    overflow: 'hidden',
    '& .MuiInputBase-root': {
      borderRadius: 0,
    },
  },
  divider: {
    borderColor: 'neutral.700',
  },
  agentIdRow: {
    border: '1px solid',
    borderColor: 'neutral.200',
    borderRadius: '10px',
    backgroundColor: 'neutral.25',
    p: '10px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  agentIdValue: {
    fontWeight: 600,
    color: 'neutral.600',
  },
  agentIdLabel: {
    color: 'neutral.300',
    fontSize: 13,
  },
  submitButton: {
    width: '100%',
    borderRadius: '10px',
    py: 1.5,
    fontWeight: 600,
    border: '1px solid',
    borderColor: 'primary.200',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
  },
};

const GolaCreateBOUsersForm = () => {
  const {
    t,
    control,
    handleSubmit,
    errors,
    isSubmitting,
    isAdmin,
    isAgent,
    selectedRole,
    selectedManagerId,
    enableSelectManagers,
    localizedRoleOptions: rawRoleOptions,
    managerOptions: rawManagerOptions,
    userIdDisplay,
    setValue,
    onSubmit,
    closeModal,
  } = useCreateBOUsersForm();

  const roleItems = rawRoleOptions.map((o) => ({ key: o.value ?? o.label, label: o.label }));
  const managerItems = rawManagerOptions.map((o) => ({ key: o.value, label: o.label }));
  const currencyItems = CURRENCY_OPTIONS.map((o) => ({ key: o.value, label: o.label }));

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={styles.container}>
      <Stack direction='row' sx={styles.header}>
        <Typography sx={styles.headerTitle}>{t('createNewUser')}</Typography>
        <IconButton onClick={closeModal} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack
        spacing={3}
        sx={{
          px: 1.5,
          mb: 2,
        }}
      >
        <Box sx={styles.section}>
          <Controller
            name={isAgent ? 'username' : 'email'}
            control={control}
            render={({ field }) => (
              <StyledInput
                {...field}
                placeholder={isAgent ? t('register.username') : t('register.email')}
                fullWidth
                error={isAgent ? !!errors.username : !!errors.email}
                helperText={isAgent ? errors.username?.message : errors.email?.message}
              />
            )}
          />
          <Divider sx={styles.divider} />
          <Controller
            name='password'
            control={control}
            render={({ field }) => (
              <StyledInput
                {...field}
                placeholder={t('register.password')}
                type='password'
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
              />
            )}
          />
        </Box>

        <Box sx={styles.section}>
          <Controller
            name='firstName'
            control={control}
            render={({ field }) => (
              <StyledInput
                {...field}
                placeholder={t('register.firstName')}
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            )}
          />
          <Divider sx={styles.divider} />
          <Controller
            name='lastName'
            control={control}
            render={({ field }) => (
              <StyledInput
                {...field}
                placeholder={t('register.lastName')}
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            )}
          />
        </Box>

        {isAdmin && (
          <StyledSelect
            value={selectedRole}
            handleChange={(e) => setValue('groupName', e.target.value)}
            itemsList={roleItems}
          />
        )}

        {enableSelectManagers && (
          <StyledSelect
            value={selectedManagerId}
            handleChange={(e) => setValue('managerId', e.target.value)}
            itemsList={managerItems}
          />
        )}

        <Stack
          sx={{
            mb: 2,
            '& .MuiFormControl-root': { p: 0 },
          }}
        >
          <StyledSelect value={CURRENCY_OPTIONS[0].value} itemsList={currencyItems} />
        </Stack>

        {isAgent && (
          <Box sx={styles.agentIdRow}>
            <Typography sx={styles.agentIdValue}>{userIdDisplay}</Typography>
            <Typography sx={styles.agentIdLabel}>Agent ID</Typography>
          </Box>
        )}

        <Button type='submit' variant='contained' disabled={isSubmitting} sx={styles.submitButton}>
          {t('register.register')}
        </Button>
      </Stack>
    </Box>
  );
};

export default GolaCreateBOUsersForm;
